/**
 * Engineer impersonation TTL monitor (spec 048, T094).
 *
 * Platform-neutral — uses only `setTimeout`/`setInterval` and `fetch`.
 * All state is injected via the callbacks rather than imported from
 * platform-specific store modules.
 *
 * While a grant is active, this monitor:
 *   1. Schedules an absolute timeout that fires `onExpired` at `expiresAt`.
 *   2. Polls every 30s via `fetchGrantStatus` to catch server-side
 *      revocations. On a typed `IMPERSONATION_GRANT_EXPIRED` /
 *      `IMPERSONATION_GRANT_INVALID` error it calls `onExpired`.
 *
 * Relocated from `frontend/src/lib/impersonation/ttl-monitor.ts` as part
 * of the auth cutover (spec 009, FILE 14, Group F).
 */

import type { GrantStatusResponse, TeardownReason } from './types.js';

const POLL_INTERVAL_MS = 30_000;
/** `setTimeout` is bounded by signed 32-bit ms (~24.8 days). Clamp defensively. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/** Typed error thrown by the API client layer when the backend rejects the grant. */
export interface GrantApiError extends Error {
  readonly code: string;
}

function isGrantApiError(err: unknown): err is GrantApiError {
  return err instanceof Error && 'code' in err && typeof (err as GrantApiError).code === 'string';
}

export interface TtlMonitorDeps {
  /** Epoch ms when the grant expires. */
  expiresAt: number;
  /** Fetch the current grant status from the backend. */
  fetchGrantStatus: () => Promise<GrantStatusResponse>;
  /** Called when the grant expires or is revoked server-side. */
  onExpired: (reason: TeardownReason) => Promise<void>;
  /** Called on each successful poll — used to refresh `expiresAt` /
   *  `lastUsedAt` in the store. */
  onStatusRefreshed: (expiresAtMs: number, lastUsedMs: number) => void;
}

/**
 * Start the TTL monitor for the currently-active grant. Returns a
 * cleanup function that clears every pending timer and the poll interval.
 * Calling the cleanup more than once is safe.
 *
 * If `expiresAt` is in the past, the expiry timer fires immediately with a
 * 0ms delay (already expired — caller will tear down on first tick).
 */
export function startTtlMonitor(deps: TtlMonitorDeps): () => void {
  let cleared = false;
  let expiryTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  // 1. Absolute expiry timeout.
  const msUntilExpiry = Math.max(0, deps.expiresAt - Date.now());
  const clampedDelay = Math.min(msUntilExpiry, MAX_TIMEOUT_MS);
  expiryTimer = setTimeout((): void => {
    expiryTimer = null;
    void deps.onExpired('expired');
  }, clampedDelay);

  // 2. Periodic poll — catches server-side revocations.
  pollTimer = setInterval((): void => {
    void (async (): Promise<void> => {
      try {
        const status = await deps.fetchGrantStatus();
        const expiresAtMs = new Date(status.expires_at).getTime();
        const lastUsedMs = new Date(status.last_used_at).getTime();
        deps.onStatusRefreshed(expiresAtMs, lastUsedMs);
      } catch (err: unknown) {
        // SILENT-CATCH: typed grant errors trigger teardown; infra errors retry next tick.
        if (isGrantApiError(err)) {
          if (
            err.code === 'IMPERSONATION_GRANT_EXPIRED' ||
            err.code === 'IMPERSONATION_GRANT_INVALID'
          ) {
            await deps.onExpired('expired');
            return;
          }
        }
        // Network errors / 5xx — leave state alone. Next poll retries.
      }
    })();
  }, POLL_INTERVAL_MS);

  return (): void => {
    if (cleared) return;
    cleared = true;
    if (expiryTimer !== null) {
      clearTimeout(expiryTimer);
      expiryTimer = null;
    }
    /* v8 ignore start -- pollTimer is assigned by setInterval at construction and only nulled here; always non-null on the single cleanup pass */
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    /* v8 ignore stop */
  };
}
